'use client';

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Combobox, ComboboxOption } from '@/components/ui-custom/combobox';
import { DatePickerFormField } from '@/components/ui-custom/date-picker';
import { useListAirlinesMinimalLazyQuery, AirlineSortField, SortOrder } from '@/gql/operations';

// Form validation schema
const flightAddSchema = z.object({
  airlineId: z.string().min(1, 'Please select an airline'),
  flightNumber: z.string()
    .min(1, 'Flight number is required')
    .regex(/^[A-Z0-9]+$/, 'Flight number must contain only letters and numbers'),
  date: z.date({
    required_error: 'Date is required',
  }),
});

type FlightAddFormData = z.infer<typeof flightAddSchema>;

interface FlightAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Airline search hook using lazy query
const useAirlineSearch = () => {
  const [searchAirlines, { data, loading }] = useListAirlinesMinimalLazyQuery();

  const searchAirlinesWithQuery = React.useCallback((query: string) => {
    if (query.length >= 1) {
      searchAirlines({
        variables: {
          filter: { search: query },
          sort: { field: AirlineSortField.NameCoalesced, order: SortOrder.Asc },
          pagination: { page: 1, limit: 10 },
        },
      });
    }
  }, [searchAirlines]);

  const airlines = React.useMemo(() => {
    if (!data?.airlines?.edges) return [];
    
    return data.airlines.edges.map(edge => ({
      id: edge.node.id,
      nameCoalesced: edge.node.nameCoalesced || '',
      iataCode: edge.node.iataCode,
      icaoCode: edge.node.icaoCode,
    }));
  }, [data]);

  return { airlines, loading, searchAirlinesWithQuery };
};

export function FlightAddDialog({ open, onOpenChange }: FlightAddDialogProps) {
  const { airlines, loading: airlinesLoading, searchAirlinesWithQuery } = useAirlineSearch();

  const form = useForm<FlightAddFormData>({
    resolver: zodResolver(flightAddSchema),
    defaultValues: {
      airlineId: '',
      flightNumber: '',
      date: undefined,
    },
  });

  const onSubmit = async (data: FlightAddFormData) => {
    try {
      console.log('Form data:', data);
      // TODO: Implement flight creation logic here
      // await createFlight(data);
      
      // Reset form and close dialog
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating flight:', error);
      // Handle error (show toast, etc.)
    }
  };

  // Convert airlines to combobox options
  const airlineOptions: ComboboxOption[] = airlines.map(airline => ({
    value: airline.id,
    label: `${airline.nameCoalesced} (${airline.iataCode || '-'} / ${airline.icaoCode || '-'})`,
    searchValue: `${airline.nameCoalesced} ${airline.iataCode || ''} ${airline.icaoCode || ''}`.toLowerCase(),
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Flight</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Airline Selection */}
            <FormField
              control={form.control}
              name="airlineId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Airline</FormLabel>
                  <FormControl>
                    <Combobox
                      options={airlineOptions}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Select airline..."
                      searchPlaceholder="Search airlines..."
                      emptyMessage={airlinesLoading ? "Loading..." : "No airlines found."}
                      onSearchChange={searchAirlinesWithQuery}
                      title="Select Airline"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Flight Number */}
            <FormField
              control={form.control}
              name="flightNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Flight Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., 1234"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date */}
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <DatePickerFormField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select flight date"
                      allowFutureDates={true}
                      minDate={new Date('1903-12-17')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Form Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Adding...' : 'Add Flight'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
} 